import { memo, useEffect, useRef, useState } from 'react';

import ReactECharts from 'echarts-for-react';

import { SectionTitle } from '@/components/common/SectionTitle';
import { DEFAULT_PERSONA } from '@/features/decisions/config/personaContext';
import type { FcfEntitlementBar, FcfEntitlementCards } from '@/features/decisions/decisions.api.types';
import { buildFcfChartOption } from '@/features/decisions/utils/fcfChartOption';
import { fmtInteger, weeksBetween } from '@/features/decisions/utils/fcfFormat';

interface FcfImpactChartProps {
  refBar: FcfEntitlementBar | undefined;
  cmpBars: FcfEntitlementBar[];
  cards: FcfEntitlementCards | undefined;
  periodFrom: string;
  periodTo: string;
  // Shared lost-sales assumption (same value that drives the Build-Up). Passed
  // debounced by the page so dragging the slider doesn't thrash the chart.
  lostSalesPct: number;
  // When true, show a fixed override value for Keystone Quantity Only instead of API delta.
  keystoneOnlyOverride?: boolean;
  overrideValue?: number;
  // Multiplier applied to all OCF bar values.
  chartMultiplier?: number;
}

// Memoized: the page re-renders on every lost-sales tick, but the chart only
// needs to redraw when its (debounced) inputs actually change.
export const FcfImpactChart = memo(function FcfImpactChart({
  refBar,
  cmpBars,
  cards,
  periodFrom,
  periodTo,
  lostSalesPct,
  keystoneOnlyOverride = DEFAULT_PERSONA.keystoneOnlyOverride,
  overrideValue,
  chartMultiplier = DEFAULT_PERSONA.chartMultiplier,
}: FcfImpactChartProps) {
  const weeks = weeksBetween(cards?.period_from ?? periodFrom, cards?.period_to ?? periodTo);
  const chartOption = buildFcfChartOption(
    refBar,
    cmpBars,
    lostSalesPct,
    keystoneOnlyOverride,
    overrideValue,
    chartMultiplier,
  );

  // ECharts measures its width once at init — which can happen while the
  // 2/3-column layout is still settling, leaving the canvas too wide so bars
  // overflow the card. Track the container width and pin the chart to it (the
  // `key` forces a clean re-init whenever the width changes).
  const wrapRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState<number | undefined>(undefined);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver((entries) => {
      const w = Math.round(entries[0].contentRect.width);
      if (w > 0) setWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <SectionTitle>Incremental Operating Cash Flow by Policy</SectionTitle>
        <span className="text-xs text-gray-500">
          {fmtInteger(cards?.skus_evaluated)} SKUs · {weeks != null ? `${weeks}w window` : '—'}
        </span>
      </div>
      <div ref={wrapRef} className="w-full overflow-hidden">
        <ReactECharts
          opts={{ renderer: 'svg' }}
          key={width}
          option={chartOption}
          style={{ height: 420, width: width ? `${width}px` : '100%' }}
          notMerge
        />
      </div>
      <div className="mt-2">
        <span
          className="text-[10px] text-gray-400"
          title="bar = Δ Operating Cash Flow vs reference = Δrevenue_demand_fulfilled + (1 − lost sales%)·Δrevenue_backlog_fulfilled − Δorder_placed_total_cost"
        >
          Bars show incremental operating cash flow vs. the Incumbent · lost sales {Math.round(lostSalesPct * 100)}%.
        </span>
      </div>
    </div>
  );
});
